(ns ui.view.card.sub
  (:require
   [ui.util :as uu]
   [re-frame.core :as rf]
   [ui.view.card.model :as model]
   [ui.state.session.sub :as session-sub]
   [common.entity :as entity]
   [common.schema.entities.pipeline :as pipeline]
   [common.schema.registry :as registry]
   [common.localization :as loc]
   [clojure.set :as set]

     ;; TODO: remove this once we have a new permissions layer in place
   [frontend.state.permissions :as state-permissions]
   [common.util :as cu]))

(rf/reg-sub
 ::card-state
 (fn [db [_ {:keys [card-state-path] :as _ctx}]]
   (get-in db card-state-path)))

(defn card-state-extractor
  [[_ ctx]]
  [(rf/subscribe [::card-state ctx])])

(rf/reg-sub
 ::full-screen?
 card-state-extractor
 (fn [[state]]
   (get-in state model/full-screen-path)))

(rf/reg-sub
 ::loading?
 card-state-extractor
 (fn [[state]]
   (-> state
       (get-in model/initial-data-loaded-path)
       not)))

(rf/reg-sub
 ::loading-error?
 card-state-extractor
 (fn [[state]]
   (get-in state model/loading-error-path)))

(rf/reg-sub
 ::entities
 card-state-extractor
 (fn [[state]]
   (get-in state model/entities-path)))

(rf/reg-sub
 ::entity-peek-open?
 card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/entity-peek-open-path)))

(rf/reg-sub
 ::entity-peek-entity-id
 card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/entity-peek-entity-id-path)))

(rf/reg-sub
 ::entity-peek-entity
 (fn [[_ ctx]]
   [(rf/subscribe [::entities ctx])
    (rf/subscribe [::entity-peek-entity-id ctx])])
 (fn [[entities peek-id]]
   (get entities peek-id)))

(rf/reg-sub
 ::delete-confirmation-entity-id
 card-state-extractor
 (fn [[card-state]]
   (get-in card-state model/delete-confirmation-entity-id-path)))

  ;; Copied from frontend.view.field-templates
(def system-fields
  [:common/migration-note
   :common/tenant
   :common/owner-role
   :common/disabled?
   :common/zap-sample?
   :common/gift-sample?
   :common/related-entities
   :common/system-kws
   :common/archived?
   :common/change-meta
   :common/ord
   :common/containing-entity
   :company/clearbit-company
   :company/fullcontact-company
   :company/intelligence-company
   :person/fullcontact-person
   :person/clearbit-person
   :person/intelligence-person
   :oppo/asset-type
   :oppo/asset-class
   :oppo/flagged-by
   :asset/type])

(def hidden-peek-entity-fields
  (set/union
   (set system-fields)
   #{:db/id
     :entity/type
     :common/lower-case-str
     :lower-case-entity
     :common/related-entities}))

  ;; TODO: discuss with Timo and PO if this is correct
(defn resolve-entity-peek-entity-template
  [{:keys [entity tenant tenant-schema]}]
  (let [entity-type (:entity/type entity)
        entity-schema (registry/get-schema tenant-schema entity-type)
        ;; A peeked pipeline entity must be permissioned against its own pipeline,
        ;; not just the tenant-global config - otherwise a field the pipeline
        ;; disabled falls back to global access and shows up here. nil for
        ;; non-pipeline entity types, which is what access-to-field expects.
        pipeline-id (pipeline/entity-pipeline entity)]
    (->> entity-schema
         (remove (fn [[field field-schema]]
                   (let [explicit? (hidden-peek-entity-fields field)
                         no-access? (not (state-permissions/field-read-access? field
                                                                               entity-type
                                                                               pipeline-id))
                         secondary-field? (:field/secondary-field? field-schema)]
                     (or explicit?
                         no-access?
                           ;; TODO: remove secondary-field?
                         secondary-field?))))
         (into {}))))

(defn resolve-entity-peek-visible-data
  [{:keys [entity entity-template entity-peek-config
           tenant tenant-schema entities-by-id]}]
  (let [visible-fields-strategy (:visible-fields-strategy entity-peek-config)
        selected-fields (->> entity-peek-config
                             :visible-fields
                             (map :field))
        all-fields (->> entity-template
                        (sort-by (comp :field/default-ord second))
                        (map first))
        resolve-field (fn [field]
                        (let [field-schema (registry/get-field-schema tenant-schema field)
                              data-type (:field/data-type field-schema)
                              value (get entity field)
                              field-label (loc/field-name field field-schema)
                              materialized-value (uu/field-value->materialized-value
                                                  {:field field
                                                   :value (get entity field)
                                                   :entity entity
                                                   :tenant tenant
                                                   :tenant-schema tenant-schema
                                                   :id->entity entities-by-id
                                                   :show-currency? true
                                                   :preserve-rich-text? true})]
                          {:ord (:field/default-ord field-schema)
                           :field field
                           :value value
                           :data-type data-type
                           :field-label field-label
                           :materialized-value materialized-value}))]
    (case visible-fields-strategy
      :all-fields
      (->> all-fields
           (mapv resolve-field))

      :fields-that-have-value
      (->> all-fields
           (mapv resolve-field)
           (filterv (fn [{:keys [materialized-value]}]
                      (not (cu/empty-value? materialized-value)))))

      :selected-fields
      (->> selected-fields
           (mapv resolve-field))

      (throw (ex-info "Unknown visible-fields-strategy"
                      {:visible-fields-strategy visible-fields-strategy})))))

(defn entity-peek-extractors
  [[_ ctx]]
  [(rf/subscribe [::entity-peek-open? ctx])
   (rf/subscribe [::entity-peek-entity-id ctx])
   (rf/subscribe [::entities ctx])
   (rf/subscribe [::session-sub/entities])
   (rf/subscribe [::session-sub/tenant])
   (rf/subscribe [::session-sub/tenant-schema])])

(defn entity-peek-computation
  [[entity-peek-open?
    peek-entity-id
    card-entities-by-id
    global-entities-by-id
    tenant
    tenant-schema]
   [_
    _ctx
    entity-peek-config]]
  (when entity-peek-open?
    (let [peek-entity (get card-entities-by-id peek-entity-id)
          entity-type (:entity/type peek-entity)
          entities-by-id (merge global-entities-by-id
                                card-entities-by-id)
            ;; TODO: handle different peek titles for different entity types
          title (cond
                  (= entity-type :entity.type/task)
                  (:task/title peek-entity)

                  (= (entity/display-string peek-entity) (str entity-type))
                  nil

                  :else (entity/display-string peek-entity))
          entity-template (resolve-entity-peek-entity-template
                           {:entity peek-entity
                            :tenant tenant
                            :tenant-schema tenant-schema})
          data (resolve-entity-peek-visible-data
                {:entity peek-entity
                 :entity-template entity-template
                 :tenant tenant
                 :tenant-schema tenant-schema
                 :entity-peek-config entity-peek-config
                 :entities-by-id entities-by-id})]
      {:entity-id peek-entity-id
       :title title
       :data data})))

(rf/reg-sub
 ::entity-peek-data
 entity-peek-extractors
 entity-peek-computation)
